/**
 * Addresses of content: `author/course/lesson`.
 *
 * A course is `anna/solfeggio`; a lesson of it `anna/solfeggio/intervals`, a topic of
 * it `anna/solfeggio/reading-music`; a lesson that stands alone has `-` where the
 * course would be: `anna/-/intervals`. The server makes them (from the author's
 * handle and from titles) and never changes them.
 *
 * An address is the id of the thing in the API and, with a slash before it, the
 * path of its page. Nothing here invents one: the helpers only put together the
 * parts of a route, and take apart what the server sent.
 */
import { useParams } from 'react-router';

/** Where a course would be, in the address of a lesson that has none. */
export const NO_COURSE = '-';

/** The page of a course or of a lesson: its address is its path. */
export const pageOf = (address: string) => `/${address}`;

/** Where a lesson's test is taken. */
export const testOf = (lesson: string) => `/${lesson}/test`;

/** Where the check of a topic is taken. A topic is `author/course/topic`. */
export function checkOf(topic: string): string {
  const cut = topic.lastIndexOf('/');
  return `/${topic.slice(0, cut)}/topics/${topic.slice(cut + 1)}/check`;
}

/** Where its author edits a course, or a lesson that stands alone. */
export const studioOf = (address: string) => `/studio/${address}`;

/** The name a thing has inside its course: the last part of its address. */
export const lastPart = (address: string) => address.slice(address.lastIndexOf('/') + 1);

/** Whether a lesson's address is that of one outside any course. */
export const standsAlone = (lesson: string) => lesson.split('/')[1] === NO_COURSE;

/** The address a route names with `:author/:course`. */
export function useCourseAddress(): string {
  const { author = '', course = '' } = useParams();
  return `${author}/${course}`;
}

/** The address a route names with `:author/:course/:lesson`; `:course` may be `-`. */
export function useLessonAddress(): string {
  const { author = '', course = '', lesson = '' } = useParams();
  return `${author}/${course}/${lesson}`;
}

/** The address a route names with `:author/:course/topics/:topic`. */
export function useTopicAddress(): string {
  const { author = '', course = '', topic = '' } = useParams();
  return `${author}/${course}/${topic}`;
}
